// HomePage.jsx
// Landing Page: VivaJour Homepage - Brand Hub
// Slug: /

import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import './LandingPages.css';

// SEO Meta Tags
const SEO = {
  title: 'VivaJour – Own Your Travel Photo Albums Forever | Privacy-First',
  description: 'Create beautiful travel albums you truly own. High-resolution PDFs, automatic organization, and complete privacy. Photos stay on your device by default. Just $19.99/year for unlimited albums.',
  slug: '/'
};

export default function HomePage() {
  useEffect(() => {
    document.title = SEO.title;
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.name = 'description';
      document.head.appendChild(metaDesc);
    }
    metaDesc.content = SEO.description;
  }, []);

  return (
    <div className="landing-page">
      {/* Navigation */}
      <nav className="landing-nav">
        <div className="landing-nav-inner">
          <Link to="/" className="landing-nav-logo">
            <span>VivaJour</span>
          </Link>
          <div className="landing-nav-links">
            <Link to="/privacy-first" className="landing-nav-link">Privacy</Link>
            <Link to="/current-trip" className="landing-nav-link">Current Trip</Link>
            <Link to="/albums" className="landing-nav-link">Albums</Link>
            <Link to="/how-it-works" className="landing-nav-link">How It Works</Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="landing-hero" style={{ paddingBottom: '40px' }}>
        <h1>Own Your Travel Albums<br />Forever</h1>
        <p className="landing-hero-subtitle">
          Create beautiful travel albums with automatic organization, download high-resolution PDFs you control, and keep your memories yours. All processing happens on your device – no cloud uploads, Cancel anytime, and you keep everything you have downloaded.
        </p>
        <div className="landing-cta-group">
          <Link to="/how-it-works" className="landing-btn landing-btn-primary">
            Start Free Trial →
          </Link>
          <Link to="/privacy-first" className="landing-btn landing-btn-secondary">
            Privacy-First Design
          </Link>
        </div>
        <div className="landing-trust-row">
          <span className="landing-trust-item">
            <svg viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
            30-day free trial
          </span>
          <span className="landing-trust-item">
            <svg viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
            $19.99/year (limited time)
          </span>
          <span className="landing-trust-item">
            <svg viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
            Photos stay on device
          </span>
        </div>
      </section>

      {/* Three Pillars */}
      <section className="landing-section landing-section-alt">
        <div className="landing-container">
          <h2 style={{ textAlign: 'center' }}>Three Ways to Build Your Album</h2>
          <p className="landing-section-subtitle" style={{ textAlign: 'center', margin: '0 auto 40px' }}>
            Whether you're traveling now or looking back – VivaJour adapts to your journey.
          </p>
          
          <div className="landing-features-grid">
            <Link to="/current-trip" className="landing-feature-card" style={{ textDecoration: 'none', transition: 'transform 0.2s, box-shadow 0.2s' }}>
              <div className="landing-feature-icon">📍</div>
              <h3>Current Trip Mode</h3>
              <p>Select date(s), all photos automatically included. No background app. Quick digests (up to 10 pages) to share immediately.</p>
              <span style={{ color: 'var(--landing-accent)', fontSize: '13px', fontWeight: 600, marginTop: '12px', display: 'inline-block' }}>Learn more →</span>
            </Link>
            
            <Link to="/albums" className="landing-feature-card" style={{ textDecoration: 'none', transition: 'transform 0.2s, box-shadow 0.2s' }}>
              <div className="landing-feature-icon">📚</div>
              <h3>Past Trip Albums</h3>
              <p>Select photos from your camera roll and let VivaJour automatically organize them into a coherent travel story.</p>
              <span style={{ color: 'var(--landing-accent)', fontSize: '13px', fontWeight: 600, marginTop: '12px', display: 'inline-block' }}>Learn more →</span>
            </Link>
            
            <Link to="/privacy-first" className="landing-feature-card" style={{ textDecoration: 'none', transition: 'transform 0.2s, box-shadow 0.2s' }}>
              <div className="landing-feature-icon">🔒</div>
              <h3>Privacy-First Design</h3>
              <p>Your photos never leave your device for processing. No cloud uploads. No AI training. You own your albums forever.</p>
              <span style={{ color: 'var(--landing-accent)', fontSize: '13px', fontWeight: 600, marginTop: '12px', display: 'inline-block' }}>Learn more →</span>
            </Link>
          </div>
        </div>
      </section>

      {/* How It Works Preview */}
      <section className="landing-section">
        <div className="landing-container">
          <h2>How VivaJour Works</h2>
          <p className="landing-section-subtitle">
            From camera roll to shareable album in minutes – all on your device.
          </p>
          
          <div className="landing-steps">
            <div className="landing-step">
              <div className="landing-step-number">1</div>
              <div className="landing-step-content">
                <h3>Select Your Photos</h3>
                <p>Choose from your camera roll or let VivaJour automatically detect trips based on dates and locations in your photos.</p>
              </div>
            </div>
            
            <div className="landing-step">
              <div className="landing-step-number">2</div>
              <div className="landing-step-content">
                <h3>Automatic Organization</h3>
                <p>Photos are grouped by location and date, creating a natural narrative flow for your travel story.</p>
              </div>
            </div>
            
            <div className="landing-step">
              <div className="landing-step-number">3</div>
              <div className="landing-step-content">
                <h3>Own Forever</h3>
                <p>Download high-res PDFs you control completely. Share via link, keep on your device, or archive however you want.</p>
              </div>
            </div>
          </div>
          
          <div style={{ textAlign: 'center', marginTop: '40px' }}>
            <Link to="/how-it-works" className="landing-btn landing-btn-secondary">
              See Full Process →
            </Link>
          </div>
        </div>
      </section>

      {/* Features Overview */}
      <section className="landing-section landing-section-alt">
        <div className="landing-container">
          <h2 style={{ textAlign: 'center' }}>Everything You Need</h2>
          
          <div className="landing-features-grid" style={{ marginTop: '40px' }}>
            <div className="landing-feature-card">
              <div className="landing-feature-icon">🗺️</div>
              <h3>Smart Trip Detection</h3>
              <p>Automatically identifies trips using GPS and date metadata embedded in your photos.</p>
            </div>
            
            <div className="landing-feature-card">
              <div className="landing-feature-icon">📖</div>
              <h3>Story-Based Albums</h3>
              <p>Photos organized into a narrative that tells the story of your journey.</p>
            </div>
            
            <div className="landing-feature-card">
              <div className="landing-feature-icon">✈️</div>
              <h3>Works Offline</h3>
              <p>Build albums anywhere – on a plane, in the mountains, or off the grid.</p>
            </div>
            
            <div className="landing-feature-card">
              <div className="landing-feature-icon">📄</div>
              <h3>High-Res PDF Export</h3>
              <p>Download your albums as high-resolution PDFs you own forever. No subscription lock-in.</p>
            </div>
            
            <div className="landing-feature-card">
              <div className="landing-feature-icon">🔗</div>
              <h3>Easy Sharing</h3>
              <p>Share your album via link or export for social media. You control access.</p>
            </div>
            
            <div className="landing-feature-card">
              <div className="landing-feature-icon">🎨</div>
              <h3>Professional Templates</h3>
              <p>Curated layouts designed for travel storytelling.</p>
            </div>
          </div>
          
          <div style={{ textAlign: 'center', marginTop: '40px' }}>
            <Link to="/templates" className="landing-btn landing-btn-secondary">
              View Templates →
            </Link>
          </div>
        </div>
      </section>

      {/* Who It's For */}
      <section className="landing-section">
        <div className="landing-container">
          <h2 style={{ textAlign: 'center' }}>Built for Travelers Who Value Ownership</h2>
          
          <div className="landing-use-cases" style={{ marginTop: '40px' }}>
            <div className="landing-use-case">
              <p className="landing-use-case-title">Solo Adventurers</p>
              <p className="landing-use-case-desc">Document personal journeys and keep your memories in files you actually own.</p>
            </div>
            
            <div className="landing-use-case">
              <p className="landing-use-case-title">Families</p>
              <p className="landing-use-case-desc">Create trip albums without uploading kids' photos to corporate servers.</p>
            </div>
            
            <div className="landing-use-case">
              <p className="landing-use-case-title">Privacy-Conscious Travelers</p>
              <p className="landing-use-case-desc">Refuse to trade memories for convenience or cloud lock-in.</p>
            </div>
            
            <div className="landing-use-case">
              <p className="landing-use-case-title">Digital Minimalists</p>
              <p className="landing-use-case-desc">A focused tool that creates files you own, not subscriptions that own you.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Ownership Undertone */}
      <section className="landing-section landing-section-alt">
        <div className="landing-container-narrow" style={{ textAlign: 'center' }}>
          <div className="landing-feature-icon" style={{ margin: '0 auto 20px', width: '64px', height: '64px', fontSize: '32px' }}>💾</div>
          <h2>Your Albums. Your Files. Forever.</h2>
          <p style={{ color: 'var(--landing-muted)', fontSize: '17px', lineHeight: '1.7', marginTop: '16px' }}>
            VivaJour creates high-resolution PDFs you download and keep forever – no cloud lock-in, no proprietary formats. Your photos process on your device, never uploaded to our servers for analysis or AI training. Cancel anytime and keep everything you've made.
          </p>
          <div style={{ marginTop: '28px' }}>
            <Link to="/privacy-first" className="landing-btn landing-btn-secondary">
              Learn About Our Privacy Approach →
            </Link>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="landing-section">
        <div className="landing-container">
          <div className="landing-callout">
            <h3>Start Owning Your Travel Memories</h3>
            <p>Try free for 30 days. Then just $19.99/year (limited time — regular $39.99).</p>
            <div className="landing-cta-group">
              <a href="https://apps.apple.com/app/id6756980207" className="landing-btn landing-btn-primary" target="_blank" rel="noopener noreferrer">
                Start Free Trial on iOS
              </a>
              <a href="https://play.google.com/store/apps/details?id=com.yourco.traveltracker" className="landing-btn landing-btn-primary" target="_blank" rel="noopener noreferrer">
                Start Free Trial on Android
              </a>
            </div>
            <p style={{ fontSize: '13px', color: 'var(--landing-muted)', marginTop: '16px' }}>
              Cancel during trial to avoid any charges. Keep all albums you create.
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="landing-footer">
        <div className="landing-footer-links">
          <Link to="/privacy-first" className="landing-footer-link">Privacy</Link>
          <Link to="/current-trip" className="landing-footer-link">Current Trip</Link>
          <Link to="/albums" className="landing-footer-link">Albums</Link>
          <Link to="/how-it-works" className="landing-footer-link">How It Works</Link>
          <Link to="/templates" className="landing-footer-link">Templates</Link>
        </div>
        <p className="landing-footer-meta">
          VivaJour Labs LLC • Built in Wisconsin
        </p>
        <p className="landing-footer-meta" style={{ marginTop: '8px' }}>
          <Link to="/privacy-policy" className="landing-footer-link">Privacy Policy</Link>
          {' • '}
          <Link to="/terms" className="landing-footer-link">Terms of Service</Link>
          {' • '}
          <Link to="/contact" className="landing-footer-link">Contact</Link>
        </p>
        <p className="landing-footer-tagline">
          © 2026 VivaJour. Your albums stay yours. Forever.
        </p>
      </footer>
    </div>
  );
}
